import React, { useState, useRef, useEffect } from 'react';

import Styled from './ProgressChart.styles';

import Text from '../../Text';
import Tag from '../../Tag';
import Box from '../../Box';
import { formatDate } from '../../../utils/dateUtils';
import { populateArray } from '../../../utils/arrayUtils';
import { COLORS } from '@/utils/colors';

// Number of data points except the last point which is always fixed 80px from the right
// most side of timeline, the rest of the area must be split evenly
const TIMELINE_LEN = 6;
const HIGHEST_INDEX = TIMELINE_LEN - 1;

const calculateTargetDateModule = (timeLineData: Date[]) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const datesAfterToday = timeLineData.map((date, index) => (
    { date, index }
  )).filter((obj) => {
    const tempDate = new Date(obj.date);
    tempDate.setHours(0, 0, 0, 0);
    return today < tempDate;
  });
  return datesAfterToday.length > 0 ? datesAfterToday[0].index : HIGHEST_INDEX;
};

const calculatePatientProgressText = (
  enrolledTime: Date,
  currentModule: number,
  currentTask: number,
  targetDateModule: number,
) => {
  if (!enrolledTime) {
    return 'Your patient has not created an account yet.';
  }
  if (currentModule === 0 && currentTask === 0) {
    return 'Your patient has made an account.';
  }
  if (currentModule >= HIGHEST_INDEX) {
    return 'Your patient has completed the program.';
  }
  const moduleDifference = targetDateModule - currentModule;
  if (moduleDifference > 1) {
    return 'Your patient is behind schedule.';
  }
  if (moduleDifference <= 0) {
    return 'Your patient is ahead of schedule.';
  }
  return 'Your patient is on track.';
};

const calculatePatientProgressTagColor = (
  enrolledTime: Date,
  currentModule: number,
  currentTask: number,
  targetDateModule: number,
) => {
  if (!enrolledTime) {
    return COLORS.gray3;
  }
  if (currentModule === 0 && currentTask === 0) {
    return COLORS.amberFont;
  }
  if (currentModule >= HIGHEST_INDEX) {
    return COLORS.violetFont;
  }
  const moduleDifference = targetDateModule - currentModule;
  if (moduleDifference > 1) {
    return COLORS.coralFont;
  }
  if (moduleDifference <= 0) {
    return COLORS.ceruleanFont;
  }
  return COLORS.jadeFont;
};


const createTimeLineData = (enrolledTime: Date) => (
  populateArray(6, enrolledTime).map((value, index) => {
    if (!enrolledTime) {
      return null;
    }
    const date = new Date(value);
    date.setDate(enrolledTime.getDate() + (7 * (index)));
    return date;
  })
);

// Since all of the positions of svgs are taken from their top-left corner
// we need to offset our last tick mark by about 80px so it fits on the line
const X_AXIS_LABEL_OFFSET = 80;

const calculateX = (widthPx: number, moduleNumber: number): number => {
  const actualWidth = widthPx - X_AXIS_LABEL_OFFSET;
  // Do Math.min here as protection incase a value over  is passed as currentModuleIndex;
  return actualWidth * (Math.min(moduleNumber, HIGHEST_INDEX) / (HIGHEST_INDEX)) + (X_AXIS_LABEL_OFFSET / 2);
};

const calculateTimelinePercent = (enrolledTime: Date, width: number, currentModule: number, currentTask: number) => {
  if (!enrolledTime) {
    return 0;
  }
  if (currentModule === 0 && currentTask === 0) {
    return 0.025;
  }
  if (currentModule >= HIGHEST_INDEX) {
    return 1.0;
  }

  const lowerEnd = calculateX(width, currentModule);
  const higherEnd = calculateX(width, currentModule + 1);

  return ((higherEnd + lowerEnd) / 2) / width;
};

type ProgressTickProps = {
  date: Date;
  currentModuleIndex: number;
  xOffset: number;
}
const ProgressTick: React.FC<ProgressTickProps> = ({ date, currentModuleIndex, xOffset }) => {
  const markerText = currentModuleIndex === HIGHEST_INDEX ? 'completed' : `level ${currentModuleIndex + 1}`;
  return (
    <>
      <Styled.YAxisText fill="#202625" x={xOffset} y="110">
        {markerText}
      </Styled.YAxisText>
      <Styled.YAxisText fill="#202625" x={xOffset} y="140">
        {date && formatDate(date, 'M/D/YYYY')}
      </Styled.YAxisText>
    </>
  );
};

type Props = {
  enrolledTime: Date;
  currentModuleNumber: number;
  currentTaskNumber: number;
}
const ProgressChart: React.FC<Props> = (props: Props) => {
  const {
    enrolledTime,
    currentModuleNumber,
    currentTaskNumber,
  } = props;
  const timeLineData = createTimeLineData(enrolledTime);

  if (timeLineData.length !== TIMELINE_LEN) {
    throw new Error('Incorrect Length');
  }

  const targetDateModule = calculateTargetDateModule(timeLineData);

  const [width, setWidth] = useState<number>(750);
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleWindowResize = () => (
      setWidth(divRef.current.offsetWidth)
    );
    handleWindowResize();
    window.addEventListener('resize', handleWindowResize);
    return () => (
      window.removeEventListener('resize', handleWindowResize)
    );
  }, []);

  const percentFilled = calculateTimelinePercent(enrolledTime, width, currentModuleNumber, currentTaskNumber);
  const xOffset = calculateX(width, targetDateModule);
  const tagColor = calculatePatientProgressTagColor(enrolledTime, currentModuleNumber, currentTaskNumber, targetDateModule);

  return (
    <>
      <Box display="inline-flex" alignItems="center" marginBottom="12px">
        <Tag
          bgcolor={tagColor}
          height="12px"
          width="12px"
          borderRadius="6px"
          margin="0 5px 0 0"
        />
        <Text>
          {calculatePatientProgressText(enrolledTime, currentModuleNumber, currentTaskNumber, targetDateModule)}
        </Text>
      </Box>
      <Box>
        <Box ref={divRef}>
          <svg width="100%" height="150" fill="none" xmlns="http://www.w3.org/2000/svg">
            {
              enrolledTime && (
                <>
                  <Styled.TargetDateText fill="#202625" x={xOffset} y="15">
                    Recommended
                  </Styled.TargetDateText>
                  <Styled.TargetDateText fill="#202625" x={xOffset} y="25">
                    progress
                  </Styled.TargetDateText>
                  <rect width="0.5" height="10" fill="#202625" x={xOffset} y="35" />
                </>
              )
            }
            <rect width={width} height="8" fill="#F3F3F3" rx="4" ry="4" y="55" />
            <rect
              key={`${percentFilled * width}`}
              width={percentFilled * width}
              height="8"
              fill={tagColor}
              rx="4"
              ry="4"
              y="55"
            >
              <animate attributeName="width" from="0" to={percentFilled * width} dur="0.75s" fill="freeze" />
            </rect>
            <g x="0" y="110">
              {
                timeLineData.map((date, index) => (
                  <ProgressTick
                    date={date}
                    currentModuleIndex={index}
                    xOffset={calculateX(width, index)}
                    key={index}
                  />
                ))
              }
            </g>
          </svg>
        </Box>
      </Box>
    </>
  );
};

export default ProgressChart;
